import { useState } from 'react'
import { api, errorText } from '../api/client'
import type { ImportResult } from '../api/types'
import { Icon } from './Icon'
import { Modal } from './Modal'

const MAX_BYTES = 2_000_000

/** Spreadsheet import — .xlsx or .csv, the two formats a book ever arrives in.
 *  Consent columns are ignored on purpose: an imported list is never permission
 *  to text, whatever a spreadsheet claims. */
export function ImportModal({ open, onClose, onDone }: {
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const [csv, setCsv] = useState('')
  // An .xlsx is held as base64 and parsed by the server; a CSV is read here.
  const [xlsx, setXlsx] = useState('')
  const [fileName, setFileName] = useState('')
  const [over, setOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<ImportResult | null>(null)

  const close = () => {
    setCsv('')
    setXlsx('')
    setFileName('')
    setError('')
    setResult(null)
    onClose()
  }

  function read(file: File | undefined) {
    setError('')
    setResult(null)
    setCsv('')
    setXlsx('')
    if (!file) return
    if (file.size > MAX_BYTES) return setError('That file is over 2 MB. Split it into smaller files.')
    if (/\.xlsx?$/i.test(file.name)) {
      if (file.name.toLowerCase().endsWith('.xls')) {
        return setError('Old .xls files aren’t supported — open it in Excel and “Save as” .xlsx or .csv.')
      }
      file.arrayBuffer().then((buffer) => {
        const bytes = new Uint8Array(buffer)
        let binary = ''
        bytes.forEach((b) => { binary += String.fromCharCode(b) })
        setXlsx(btoa(binary))
        setFileName(file.name)
      })
      return
    }
    file.text().then((text) => {
      setCsv(text)
      setFileName(file.name)
    })
  }

  async function run() {
    setBusy(true)
    setError('')
    try {
      const r = xlsx ? await api.importContactsXlsx(xlsx) : await api.importContacts(csv)
      setResult(r)
      if (r.created) onDone()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  const rows = csv ? Math.max(0, csv.trim().split(/\r?\n/).length - 1) : 0

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import contacts"
      subtitle="CSV with a header row. Recognised columns: first name, last name, name, email, phone, address, city, state, zip, notes."
      width={560}
      footer={
        result ? (
          <button className="btn primary" onClick={close}>Done</button>
        ) : (
          <>
            <button className="btn" onClick={close}>Cancel</button>
            <button className="btn primary" disabled={(!csv && !xlsx) || busy} onClick={run}>
              {busy ? 'Importing…' : xlsx ? 'Import this workbook' : `Import ${rows || ''} row${rows === 1 ? '' : 's'}`}
            </button>
          </>
        )
      }
    >
      {result ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="notice positive">
            <Icon name="check_circle" />
            <span>
              <strong>{result.created}</strong> added · {result.duplicates} already in the book
              {result.error_count ? ` · ${result.error_count} skipped` : ''}
            </span>
          </div>
          {result.errors.length ? (
            <div className="card" style={{ padding: 12, fontSize: 12.5, maxHeight: 200, overflowY: 'auto' }}>
              {result.errors.map((e, i) => <div key={i} style={{ padding: '2px 0' }}>{e}</div>)}
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label
            className={`dropzone${over ? ' over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(true) }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); read(e.dataTransfer.files[0]) }}
          >
            <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden onChange={(e) => read(e.target.files?.[0])} />
            <Icon name="upload_file" size={26} style={{ color: 'var(--accent)' }} />
            <div style={{ marginTop: 6 }}>
              {fileName
                ? <><strong>{fileName}</strong>{xlsx ? '' : ` · ${rows} rows`}</>
                : <>Drop an Excel or CSV file here, or <span style={{ color: 'var(--accent)' }}>choose a file</span></>}
            </div>
          </label>
          <div className="notice accent">
            <Icon name="verified_user" />
            <span>
              Imported contacts start with <strong>no SMS consent</strong>, even if the file has an opt-in
              column. Record consent on each contact once you have it. Duplicates (same email or phone) are skipped. Up to 500 rows per file.
            </span>
          </div>
          {error ? <div className="notice danger"><Icon name="error" /><span>{error}</span></div> : null}
        </div>
      )}
    </Modal>
  )
}
